import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { Title } from "@angular/platform-browser";
import * as Fathom from "fathom-client";
import OlMap from "ol/Map";
import { Observable, of } from "rxjs";
import { environment } from "src/environments/environment";
import { PlokkService } from "../app/services/plokk.service";
import { UserService } from "../app/services/user.service";
import { actionViewItem, ActionViewItemType, ApplicationGroup, AppText, AppTexts, CookieConsentValue, Languages, LayerType, mapLayer } from "./interfaces/common";
import { EditFeatureService } from "./map/query-views/services/edit-feature.service";
import { AppLoadService } from "./services/app-load.service";
import { DisplayService } from "./services/display.service";
import { LayerService } from "./services/layer.service";
import { LayoutService } from "./services/layout.service";
import { PDFService } from "./services/pdf.service";
import { MainActionViewService } from "./services/main-action-view.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, AfterViewInit {
  selText: AppText = {};
  settings: any;
  appTexts: AppTexts = {};
  mapLayers: mapLayer[] = [];
  secondaryMapLayers: mapLayer[] = [];
  urlParams: Map<string, any> | null  = null;
  applicationName: string = ""; //applicationName is the current application we are in now
  applicationSettings: any = null; //applicationSettings is all the settings only for this current application
  mapObject: OlMap = new OlMap();
  lang: Languages = Languages.Icelandic;
  userLayerName: string = "";
  activateSwitchApplication: boolean = false;
  /*
    stopProgram is just so that white screen is displayed and nothing else if user
     is not allowed in(non-existing application & not in allowedHosts & not in right group to see this app). 
     See app-template for where it is used.
  */
  stopProgram: boolean = false;

  uploadFilesActive: boolean = false;
  addLayerActive: boolean = false;
  reloadLayersWithProxy: number[] = [];
  mobileViewBreakpoint: number = 900;
  isMobileView: boolean = false;
  /*
    Will be true if user logged in to a application with allowedGroups present and the user is not in the
    allowed groups to see this application.
  */
  userNotInAllowedGroup: boolean = false;
  //Says if this application is using allowedGroups or not.
  isRestrictedGroupApp: boolean = false;
  showLoginPrompt: boolean = false;
  cookieConsent: boolean = false;
  showDash: boolean = false;
  bannerDismissed: boolean = false;

  initialURL: string = window.location.href

  apps$: Observable<ApplicationGroup[]> = of([]); // Available apps in the switch application menu, fetched from backend

  @HostListener("window:resize", ["$event"]) resizeHandler(_event: Event) {
    //console.log(`Dimensions ${window.innerWidth} x ${window.innerHeight}`)
    this.isMobileView = window.innerWidth < this.mobileViewBreakpoint;
  }

  @ViewChild("appWrapper") appWrapper: ElementRef<any> = new ElementRef<any>(
    null
  );

  constructor(
    private appSettingsService: AppLoadService,
    private titleService: Title,
    public displayService: DisplayService,
    private userService: UserService,
    private layerService: LayerService,
    private plokkService: PlokkService,
    private pdfService: PDFService,
    private editFeatureService: EditFeatureService,
    private mainActionViewService: MainActionViewService,
    public layoutService: LayoutService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (!environment.production) {
      //console.log(this.settings);
      //console.log(`Dimensions ${window.innerWidth} x ${window.innerHeight}`)
    }
    this.settings = this.appSettingsService.getSettings();
    this.urlParams = this.appSettingsService.getUrlParams();
    /*
      Loads and starts Fathom analytics. 
      Production site ID: BYWUIUKH
      Staging(prufa) site ID: DZYMGYBH
    */
    Fathom.load("BYWUIUKH")
    
    const isAllowedIn: boolean = this.checkHostRestriction(this.settings);
    if (isAllowedIn === false) {
      alert(
        `You are not allowed to enter this application from: ${window.location.hostname}`
      );
      this.stopProgram = true;
      return;
    }

    this.applicationName = this.settings.application;
    
    for (let i = 0; i < this.settings.applications.length; ++i) {
      if (this.settings.applications[i].application == this.applicationName) {
        this.applicationSettings = this.settings.applications[i];
        this.displayService.setAppSettings(this.applicationSettings);
        this.userService.setApplicationsSettings(this.applicationSettings)
        break;
      }
    }

    /* TEMP while we still have type "VEKTOR" in db */
    this.mapLayers = this.settings.mapLayers.filter((ml: mapLayer) => ml.type !== LayerType.VEKTOR)
    //Ordering for the mapLayers
    this.mapLayers = this.mapLayers.sort(function (a: any, b: any) {
      return a.layerorder - b.layerorder;
    });
    this.mapLayers = this.mapLayers.slice(0);

    this.applyURLParamsToConfigObj()

    if (this.applicationSettings !== null && this.applicationSettings !== undefined) {
      if ("option" in this.applicationSettings) {
        if ("lang" in this.applicationSettings.option) {
          this.lang = this.applicationSettings.option.lang;
        }
        if ("allowLogin" in this.applicationSettings.option) {
          this.displayService.setUserLoginAvailable(
            this.applicationSettings.option.allowLogin
          );
        }
      }

      this.appTexts = this.settings.appTexts;

      this.selText = this.appTexts[this.lang];
    }
    else{
      alert("No application or wrong application choosen");
      this.stopProgram = true;
    }
    this.displayService.setLanguage(this.lang);
    this.layerService.setLanguage(this.lang)
    this.displayService.setDynamicURL();
    this.displayService.setHideAbout();
    this.displayService.setShowLocation();
    this.displayService.setMapviewInterface();
    this.displayService.setPreferredColorTheme(this.applicationSettings?.option?.theme);
    this.editFeatureService.setMapLayer(this.mapLayers);
    this.editFeatureService.setSelText(this.selText);
    this.displayService.addSeltext(this.selText);
    this.pdfService.addSeltext(this.selText);
    this.editFeatureService.setApplicationSettings(this.applicationSettings);
    this.editFeatureService.setSnapOnItems();
    this.layerService.setApplicationSettings(this.applicationSettings);
    this.layerService.initializeMaplayersState(this.mapLayers)

    //Use typeof since 0 is a valid value aswell
    if (typeof(this.applicationSettings?.option?.map?.mainActionViewScreenProportion) === "number") {
      this.layoutService.setMainActionViewSettings(this.applicationSettings.option.map.mainActionViewScreenProportion)
    }
    
    //if (this.applicationSettings?.option?.disableMainActionView === true) this.displayService.setMainActionViewActive(false);
    //else this.displayService.setMainActionViewActive(true);
    if (this.applicationSettings?.option?.mainActionView?.showLayers !== false) {
      //this.displayService.setMainActionViewActive(true)
      const layerList: actionViewItem = { type: ActionViewItemType.mapsLayerList, text: this.selText.maps, closeable: false, hidden: true}
      this.mainActionViewService.sendNewMainActionViewItem(layerList)
    }

    if (this.applicationSettings?.option?.allowedGroups && !this.applicationSettings?.option?.allowReadAccess) {
      this.isRestrictedGroupApp = true;
      this.userService.setisRestrictedGroupApp(true);
      //If allowedGroups is defined, then allowLogin should always be true as well. Setting it here to true even if it is not defined in config.
      this.displayService.setUserLoginAvailable(true);
    }

    /*
      Alot of user management in this subscription:
      If isLoggedIn is null, then that is the initial value. If it emits true at the start of the program, then
      user is already logged in, so check for groups if this is an application where allowedGroups is defined.
    */
    this.userService
      .getIsUserLoggedIn()
      .subscribe((isLoggedIn: boolean | null) => {
        //console.log("isLoggedIn in App.component: " + isLoggedIn)
        /* If not logged in and this app has allowedGroups defined, stop the execution of mapview and take user to the login screen. */
        if (isLoggedIn === null && this.userService.getisRestrictedGroupApp()) {
          this.stopProgram = true;
          this.showLoginPrompt = true;
        }

        /*
          if user isn't logged into an app that allows read access the link might include a layer that needs authorization
          If user is already logged in then any layer that need authorization will be removed
        */
        if (this.urlParams?.get("layers") && !isLoggedIn && this.applicationSettings.option?.allowReadAccess) {
          this.urlParams.get("layers").forEach((layerId: any) => {
            const mapLayer = this.mapLayers.find((layer) => layer.id === layerId);
            if (mapLayer?.option?.Authorization && mapLayer?.option?.mp_layer?.edit?.editable) {
              this.stopProgram = true;
              this.showLoginPrompt = true;
            }
          });
        }

        /* 
          If user is currently logged in, but the current application does not allow user log in,
          then log them out. This can happen when user was in another application and logged in there, then
          changes the application to a non-login application, then we want to log them out of there.
        */
        if (isLoggedIn === true && this.displayService.getUserLoginAvailable() === false) {
          this.userService.logout();
          return;
        }
        /*
        If user is currently logged in and this is an application where allowedGroups is defined, then check
        if this user is in the right group to see this application. If not allowed in, stop execution and
        display notification to user why he is not allowde in.
      */

        if (isLoggedIn === true) {
          if (this.applicationSettings?.option?.allowedGroups && !this.applicationSettings?.option?.allowReadAccess) {
            const isAllowedIn: boolean = this.isUserInAppGroup(
              this.userService.getUserAllowedGroups(),
              this.applicationSettings?.option?.allowedGroups
            );
            if (isAllowedIn) {
              this.stopProgram = false;
              this.userNotInAllowedGroup = false;
              this.showLoginPrompt = false;
            } else {
              //Stops the program here
              this.stopProgram = true;
              //Displays notification-popup informing user why he can't continue
              this.userNotInAllowedGroup = true;
              //Don't display login-prompt. (User will be redirected to login prompt by clicking OK on notificationPopup)
              this.showLoginPrompt = false;
            }
          }
          this.userService.determineUserAccess()
          //Always set idle timeoutTime if isLoggedIn is true.
          this.userService.setTimeoutTime(
            this.applicationSettings?.option?.sessionTimeoutTime
          );
          
        }
      });

    /* Take user to login-screen if this Subject emits. Current use case: 
       * Take user to login-prompt in plokkinu 
       * Take user to login-prompt when he tried to log in to application which he does not have access to(allowedGroups...)
         We take the user to the login prompt again when he clicks "OK"-button on the info card that explains why hes not allowed in.
    */
    this.userService.getSetAppToLoginScreen().subscribe(() => {
      /*
        When we are taken back to login screen then the map.component containing the openLayers map is destroyed. However it seems
        that the OL Map is still alive in memory which is a problem when user logs for example back in(for the second time) to the app
        thorugh LMI-login, then the map is all messed up. This is not a problem for logging in with google or íslykill since a page
        refresh is needed anyway. setTarget(element) is usually used to append map to some HTML element, but calling it with null will
        ensure that it decouples from (non-existing) element and releases all resources relating to it from memory.
        See here https://github.com/openlayers/openlayers/issues/9556: 
      */
      this.mapObject.setTarget(undefined);
      this.stopProgram = true;
      this.showLoginPrompt = true;
      this.changeDetector.detectChanges();
    });

    if (this.applicationSettings?.option?.subApp) {
      this.displayService.setSubAppActive(true);
      this.plokkService.setAppSettings(
        this.applicationSettings.option.subApp,
        this.applicationName
      );
    }

    /* if custom layout is defined then set them into layoutService and apply. */
    if (this.applicationSettings?.option?.appLayout) {
      this.layoutService.setCustomLayoutActive(true);
      this.layoutService.setLayoutSettings(this.applicationSettings.option.appLayout);
    }

    this.isMobileView = window.innerWidth < this.mobileViewBreakpoint ? true : false;
    this.displayService.setIsMobileView(this.isMobileView);
    //console.log("isMobileView: " + this.isMobileView)
    /*
      layersThatNeedProxy is an array of indexes into layers in the MapsAvilable in map.component.
      These layers we will reload with a proxyUrl instead of regular URLs, for screenshot purposes, we do this to avoid
      tainted canvas problems. Lifecycle: initial emit in PDF.service of layers that need proxy -> Assign to this.reloadLayersWithProxy ->
      Triggers changeDetection in Map.component. -> Map.component reloads the layers with proxy URLs 
    */
    this.pdfService
      .getReloadLayersWithNewURL()
      .subscribe((layersThatNeedProxy: number[]) => {
        // console.log("Received layers that need new URL: ", layersThatNeedProxy)
        this.reloadLayersWithProxy = layersThatNeedProxy;
      });

    this.appSettingsService.setAuthGroupInfo(
      this.applicationSettings?.owner_group
    );

    const cookieConsentValue = this.displayService.getCookieConsent();

    if (cookieConsentValue?.consentValue !== CookieConsentValue.Rejected) {
      const consentDate = cookieConsentValue.consentDate;
      const lastUpdated = this.selText.cookieConsentLastUpdated;

      if (!(consentDate && lastUpdated && this.displayService.hasConsentBeenUpdated(consentDate, lastUpdated))) this.cookieConsent = true;
    }

    this.apps$ = this.displayService.getAvailableApplications(this.applicationName, this.settings);
    this.syncDashLayout();
  }

  ngAfterViewInit(): void {
    //Get value from CSS variable defined in stylesheet in TS
    //console.log(getComputedStyle(this.appWrapper.nativeElement).getPropertyValue("--appRowSizes"))
    //Set CSS variable from TS:
    /* 
      Setting the custom layout from config, to the CSS variable appRowSizes that is defined/lives in app.component CSS file
      Update 23.02.24 We now set this CSS variable on the root HTML tag instead of the <div> appWrapper element, why?
      because when we are in login-prompt then that div element does not exist yet and we get error when trying to set the variable.
      (Does not matter for island.is and google login, since a page refresh is needed anyway, but would fail for LMI-login)
    */
    //this.appWrapper.nativeElement.style.setProperty("--appRowSizes", this.layoutService.getAppLayoutHeights())
  }

  private syncDashLayout(): void {
    const configuredDashHeight: number = this.layoutService.getDashHeight();
    const appLayout = this.applicationSettings?.option?.appLayout;
    let hasBannerText = false;
    if (appLayout?.dashBannerType) {
      const text = this.lang === Languages.Icelandic ? appLayout.dashTextIs : appLayout.dashTextEn;
      hasBannerText = Boolean((text ?? '').trim());
    }
    const hasDashContent: boolean = hasBannerText || Boolean(this.applicationSettings?.option?.subApp?.subAppName);
    const dashHeight: number = (hasDashContent && !this.bannerDismissed) ? configuredDashHeight : 0;
    this.showDash = dashHeight > 0;
    document.documentElement.style.setProperty("--appRowSizes", this.layoutService.getAppLayoutHeights(dashHeight));
  }

  collapseDash(): void {
    this.bannerDismissed = true;
    this.showDash = false;
    document.documentElement.style.setProperty("--appRowSizes", this.layoutService.getAppLayoutHeights(0));
  }

  /**
   * Uses urlParams to possibly mutate values in this.settings (which contains settings for the application and layers)
   * @returns void
   */
  private applyURLParamsToConfigObj(): void {
    if (!this.urlParams) {
      console.warn("Could not find valid URL parameters to apply to config obj")
      return
    }
    if (this.urlParams.get("lang")) {
      this.applicationSettings.option ??= { lang: Languages.Icelandic }
      this.applicationSettings.option.lang = this.urlParams.get("lang");
    }

    if (this.urlParams.get("zoom")) {
      this.applicationSettings.option ??= { map: { zoom: 0 } };
      this.applicationSettings.option.map ??= { zoom: 0 }
      this.applicationSettings.option.map["zoom"] = this.urlParams.get("zoom");
    }
    if (this.urlParams.get("center")) {
      this.applicationSettings.option ??= { map: { center: [0, 0] } };
      this.applicationSettings.option.map ??= { center: [0, 0] };
      this.applicationSettings.option.map["center"] = this.urlParams.get("center");
    }
    if (this.urlParams.get("layers")) {
      //If layers queryparameter exists, then it controls checked layers, set all layers checked to false
      this.mapLayers.forEach((ml: mapLayer) => ml.checked = false)

      const selectedLayersID: (string | number)[] = this.urlParams.get("layers")
      const regularSelectedLayersID: number[] = selectedLayersID.filter((slID: string | number) => typeof slID === "number")
      regularSelectedLayersID.forEach((rslID: number) => {
        const layer: mapLayer | undefined = this.mapLayers.find((ml: mapLayer) => ml.id === rslID)
        if (!layer) return
        layer.checked = true
        //Track what layers are being shared via a URL
        Fathom.trackEvent(`StartLayerURL: ${layer.name}`)
      })

      /*
        Special handling for when layers array has items with "_s" appended behind the layer id.
        Means that they should be stuck in the active layers group.
      */
      const stuckLayersID: string[] = selectedLayersID.filter((slID: string | number) => typeof slID === "string")
      const stuckLayers: mapLayer[] = stuckLayersID.map((stuckLayerID: string): mapLayer | null => {
        const [layerID, stuck, unchecked] = stuckLayerID.split("_")
        if (!layerID) return null
        if (stuck !== "s") return null
        const stuckLayer: mapLayer | undefined = this.mapLayers.find((ml: mapLayer) => ml.id == +layerID)
        if (!stuckLayer) return null
        stuckLayer.checked = unchecked === "u" ? false : true
        return stuckLayer
      }).filter((sl: mapLayer | null) => sl !== null)
      this.layerService.setStuckLayers(stuckLayers)
      //Map "layers" queryParameter such that its only number layer IDs, discard "..._s_u" if its there since we ahve already proccessed that
      const onlyNumberLayerIDs: number[] = selectedLayersID.map(((slID: string | number) => {
        if (typeof slID === "number") return slID
        const layerID: string = slID.split("_")[0]
        return +layerID
      }))
      this.urlParams.set("layers", onlyNumberLayerIDs)

      const opacityMap = this.urlParams.get("opacity");
      //Set opacity from URL
      if (opacityMap) {
        for (let i = 0; i < this.mapLayers.length; i++) {
          const opacityForThisLayer: number | undefined = opacityMap?.[this.mapLayers[i].id]
          if (opacityForThisLayer !== undefined) {    // Check if opacity is defined for this layer in the opacityMap
            //console.log("Found URL opacity defined for layerId: " + this.mapLayers[i].id + " with value: " + opacityForThisLayer)
            if (opacityForThisLayer >= 0 && opacityForThisLayer <= 1) { // Check if opacity is a valid number between 0 and 1
              this.mapLayers[i].option ??= {layer: {opacity: opacityForThisLayer}}
              this.mapLayers[i].option.layer ??= {opacity: opacityForThisLayer}
              this.mapLayers[i].option.layer.opacity = opacityForThisLayer; 
            }
          }
        }
      }

      const layerIds: number[] = this.urlParams.get("layers");
      const layersFromls: mapLayer[] = layerIds.map((lID: number) => this.mapLayers.find((ml: mapLayer) => ml.id === lID))
                                       .filter((ml: mapLayer | undefined) => ml !== undefined && !ml?.basemap) as mapLayer[]
      
      /*
        We want to maintain the order of layers as they are in ls.
        if groupActiveLayers is off -> Since we only know the layerorder of the checked layers from ls we take them
        and remove from mapLayers, then reinsert them at the top of the layerList(in the same order as they are in ls)
        then recalculate layerorder for all layers. (Maybe in future it would be wiser to only allow layerorder from URL to be respected
        when activeLayerGroups is active).
      */
     //TODO 10.06.26 Look at this....
      if (this.applicationSettings?.option?.groupActiveLayers) {
        const reorderedLayers: mapLayer[] = layersFromls.reduce((acc: mapLayer[], currentVal: mapLayer) => {
          const index: number = this.mapLayers.indexOf(currentVal)
          if (index === -1) return acc
          const removedLayer: mapLayer = this.mapLayers.splice(index, 1)[0]
          acc.unshift(removedLayer)
          return acc
        }, [])
        
        reorderedLayers.forEach((ml: mapLayer) => this.mapLayers.unshift(ml))
        this.mapLayers.forEach((ml: mapLayer, i: number) => {
          ml.layerorder = i
        })
      }  
      
    }

    if (this.urlParams.get("expandedGroups")) {
      const expandedGroups: number[] = this.urlParams.get("expandedGroups")
      expandedGroups.forEach((expandedGroup: number) => {
        const groupLayer: mapLayer | undefined = this.mapLayers.find((ml: mapLayer) => ml.id === expandedGroup)
        if (!groupLayer) return
        groupLayer.option ??= { explode: true }
        groupLayer.option.explode = true
      })
    }
  }
  
  /*
    Should after a user has logged in to an application where "allowedGroups" is defined,
    check if user is allowed into this application or not. Returns true if user is allowed to continue,
    returns false if user is not allowed to go in.
  */
  isUserInAppGroup(groupsUserIsIn: string[], groupsAllowedInApplication: string[]): boolean {
    if (!groupsUserIsIn || !groupsAllowedInApplication) {
      console.warn("groupsUserIsIn || groupsAllowedInApplication was undefined, returning out early with false");
      return false;
    }

    const groupsInBothLists: string[] = groupsUserIsIn.filter(
      (guii: string) => {
        if (groupsAllowedInApplication.includes(guii)) return true;
        return false;
      }
    );

    if (groupsInBothLists.length > 0) return true;
    return false;
  }

  /* Executed when user was denied entry because hes not int he right group to see this application. */
  userDeniedEntryComingBack(): void {
    //this.stopProgram = true
    this.showLoginPrompt = true;
    //Reset userNotInAllowedGroups back to false(the test will run again if user tries to log in again)
    this.userNotInAllowedGroup = false;
    //Log out user when he is back at the login screen.
    this.userService.logout();
  }
  
  reloadMapview(): void {
    // Create a Promise that polls the server for updated map settings with specific max attempts and interval
    const pollPromise = new Promise<any>((resolve, reject) => {
      let counter = 0;
      const maxCount = 5;

      // Polling function: checks whether user-specific mapLayers have been loaded
      const poll = async (): Promise<void> => {
        try {
          await this.appSettingsService.initializeApp();
          const settings = this.appSettingsService.getSettings();

          // Check if user-specific layers exist, identified by presence of a username
          if (settings.mapLayers.some((l: any) => l.username != null)) { resolve(settings); }
          else {
            counter++;
            if (counter >= maxCount) { reject("Timeout reached"); } // Max attempts reached without success
            else { setTimeout(() => { void poll(); }, 500); }       // Retry after 500ms
          }
        } catch (error) { reject(error); }
      };

      void poll();
    });

    pollPromise
      .then((settings) => {
        this.settings = settings;
        this.userService.setUserData(settings.userData);
        this.mapLayers = settings.mapLayers.sort((a: any, b: any) => a.layerorder - b.layerorder);

        if (this.userLayerName) { this.displayService.toggleAddLayerActive(); }
      })
      .catch((error) => { console.error("Mapview reload failed:", error); });
  }

  toggleText(lang: Languages): void {
    this.lang = lang;
    this.selText = this.appTexts[lang];
    for (let i = 0; i < this.mapLayers.length; ++i) {
      this.mapLayers[i].title = this.mapLayers[i].title_lang[lang];
    }

    this.displayService.addSeltext(this.selText)
    this.editFeatureService.setSelText(this.selText)
    this.pdfService.addSeltext(this.selText)
    this.displayService.setLanguage(this.lang);
    this.layerService.setLanguage(this.lang);
    this.syncDashLayout();
  }

  byteBuffer(ds1: string): Uint8Array {
    const ds = new Uint8Array(ds1.length);
    for (let i = 0; i < ds1.length; ++i) {
      ds[i] = ds1.charCodeAt(i);
    }
    return ds;
  }

  updateMap(map: OlMap): void {
    this.mapObject = map;
  }

  updateSwitchApplication(active: boolean): void {
    this.activateSwitchApplication = active;
  }

  toggleTitle(title: string): void {
    this.setTitle(title);
  }

  public setTitle(newTitle: string): void {
    this.titleService.setTitle(newTitle);
  }

  checkHostRestriction(settings: any): boolean {
    let isAllowedIn: boolean = true;
    const application: string = settings.application;
    const allowedHosts: string[] = this.settings?.applications?.find(
      (app: any) => app.application === application
    )?.option?.allowedHosts;
    if (allowedHosts === undefined) {
      return isAllowedIn; //If there is no field allowedHosts, then everything is allowed
    }

    const userHostName: string = window.location.hostname;

    if (!allowedHosts.includes(userHostName)) {
      isAllowedIn = false;
    }

    // console.log("user from host: " + userHostName + " is allowed in since the hostname was found in: " allowedHosts)
    // console.log(allowedHosts)

    return isAllowedIn;
  }

  setUserLayerSettingsData(e: any): void {
    this.userLayerName = e;
  }

  resetInputForAddLayer(): void {
    this.userLayerName = "";
  }
}